/**
 * Talk-down v1 transport (ENG-2135): upload a short recording to admin, which
 * dispatches it to the site's connector as a Command, then poll the site's
 * action_status until that Command reports whether the camera played it.
 *
 * Only this module knows the wire format. The hold-to-record control
 * (useAudioRecorder) just yields a Blob, so a later live push-to-talk can
 * swap this transport for a relay publish without touching the control.
 */
import { AxiosError } from "axios";
import { createApi } from "../../utils/createApi";
import { createMonitoringAPICall } from "../../utils/createMonitoringAPICall";

/** The Command action name, as admin stores and filters it (underscore). */
export const TALK_DOWN_ACTION = "talk_down";

/** Admin refuses larger uploads; refuse client-side first. */
export const TALK_DOWN_MAX_BYTES = 1024 * 1024;

/** Recording cap; the recorder stops itself when it is reached. */
export const TALK_DOWN_MAX_SECONDS = 30;

export const TALK_DOWN_POLL_INTERVAL_MS = 1000;
export const TALK_DOWN_POLL_TIMEOUT_MS = 30_000;

/** 202 body: the Command was queued for the connector, nothing played yet. */
export interface TalkDownDispatch {
  command_id: number;
  clip_id: number | string | null;
  site_id: number;
}

/** What the connector reports back on the Command. */
export interface TalkDownResult {
  camera_id?: number | string | null;
  clip?: string | null;
  status?: "played" | "failed" | null;
  seconds_sent?: number | null;
  codec?: string | null;
  rtp_channel?: number | null;
  error_code?: string | null;
  detail?: string | null;
}

/** One row of GET customer/{site_id}/action_status/?action=talk_down. */
export interface TalkDownStatusRow {
  id: number | string;
  action?: string;
  status: "pending" | "success" | "failed" | null;
  result: TalkDownResult | null;
  request_date?: string;
  completed_at?: string | null;
}

export const TALK_DOWN_TOO_LARGE_MESSAGE =
  "That recording is too large to send (1 MB limit). Try a shorter message.";
export const TALK_DOWN_SENT_MESSAGE = "Sent. Waiting for the camera...";
export const TALK_DOWN_NOT_RECEIVED_MESSAGE =
  "Couldn't confirm the talk-down was received";
export const TALK_DOWN_TIMEOUT_MESSAGE =
  "No result yet, the camera may still play it";
export const TALK_DOWN_IN_PROGRESS_MESSAGE =
  "Another talk-down is playing on this camera";
export const TALK_DOWN_FAILED_MESSAGE = "Talk-down failed";
export const TALK_DOWN_UNAVAILABLE_MESSAGE =
  "Talk-down isn't available right now. Try again in a moment.";
export const TALK_DOWN_SITE_INACTIVE_MESSAGE = "This site isn't active";
export const TALK_DOWN_DISPATCH_FAILED_MESSAGE =
  "Couldn't start talk-down, try again";
/** No HTTP response at all: network down, CORS, DNS. */
export const TALK_DOWN_NETWORK_MESSAGE =
  "Couldn't reach the talk-down service. Check your connection and try again.";
/** A 5xx the specific codes above don't cover. */
export const TALK_DOWN_SERVER_ERROR_MESSAGE =
  "The talk-down service had a problem (server error). Try again in a moment.";
/** An unmapped 4xx from START: the request itself was refused. */
export const TALK_DOWN_REFUSED_MESSAGE =
  "Couldn't start talk-down (request refused).";
/** The camera's Command finished failed with no code or detail we know. */
export const TALK_DOWN_CAMERA_FAILED_MESSAGE =
  "The camera didn't play the talk-down.";
/** Staging build with Use production off (dev/redesign only). */
export const TALK_DOWN_NEEDS_PRODUCTION_MODE_STAFF_MESSAGE =
  "Talk-down uses real cameras. Switch on Use production to try it.";
export const TALK_DOWN_NEEDS_PRODUCTION_MODE_MESSAGE =
  "Talk-down isn't available on this test site.";

/** Endpoint labels for the staff detail line. */
export const TALK_DOWN_CLIP_ENDPOINT_LABEL = "talk_down";
export const TALK_DOWN_LIVE_ENDPOINT_LABEL = "talk-down live";

/**
 * Staff-only technical line for a failed START: endpoint, HTTP status and the
 * backend error code, e.g. "talk_down POST 502 dispatch_failed", or
 * "talk_down POST · no response ERR_NETWORK".
 */
export const describeTalkDownErrorDetail = (
  reason: unknown,
  endpoint: string = TALK_DOWN_CLIP_ENDPOINT_LABEL
): string | undefined => {
  const err = reason as
    | (AxiosError<{ error?: string; error_code?: string }> & {
        isProductionModeBlock?: boolean;
      })
    | undefined;
  if (err?.isProductionModeBlock) return `${endpoint} POST blocked (read-only)`;
  const status = err?.response?.status;
  const code = err?.response?.data?.error ?? err?.response?.data?.error_code;
  if (status)
    return [`${endpoint} POST ${status}`, code].filter(Boolean).join(" ");
  const errCode = (reason as { code?: string } | undefined)?.code;
  const isAxios = Boolean(
    (reason as { isAxiosError?: boolean } | undefined)?.isAxiosError
  );
  if (isAxios || errCode) {
    return `${endpoint} POST · no response${errCode ? ` ${errCode}` : ""}`;
  }
  return undefined;
};

/** Staff-only line for a finished clip Command: "job status=failed error_code=…". */
export const describeTalkDownOutcomeDetail = (
  row: TalkDownStatusRow | null
): string | undefined => {
  if (!row) return "job · no result before timeout";
  const result = row.result ?? {};
  return [
    `job status=${result.status ?? row.status ?? "?"}`,
    result.error_code ? `error_code=${result.error_code}` : "",
  ]
    .filter(Boolean)
    .join(" ");
};

/** Staff-only line for a finished live session. */
export const describeLiveTalkDownOutcomeDetail = (
  status: LiveTalkDownStatus | null
): string | undefined => {
  if (!status) return undefined;
  return [
    `session status=${status.status}`,
    status.error_code ? `error_code=${status.error_code}` : "",
  ]
    .filter(Boolean)
    .join(" ");
};

const fileNameFor = (mimeType: string): string => {
  const type = mimeType.toLowerCase();
  if (type.includes("ogg")) return "talk-down.ogg";
  if (type.includes("mp4") || type.includes("aac")) return "talk-down.m4a";
  if (type.includes("wav")) return "talk-down.wav";
  return "talk-down.webm";
};

/**
 * POST camera/{camera_id}/talk_down/ (multipart, field `audio`). Resolves
 * with the 202 body. Throws the axios error on anything else; use
 * describeTalkDownSendError for the operator message.
 */
export const sendTalkDown = async (
  cameraId: string | number,
  audio: Blob
): Promise<TalkDownDispatch> => {
  if (audio.size > TALK_DOWN_MAX_BYTES) {
    throw new Error(TALK_DOWN_TOO_LARGE_MESSAGE);
  }
  const form = new FormData();
  form.append("audio", audio, fileNameFor(audio.type));
  // createApi defaults Content-Type to application/json; axios drops that
  // header for a FormData body in the browser so the boundary gets set.
  const response = await createApi("").post(
    `camera/${cameraId}/talk_down/`,
    form
  );
  return response.data as TalkDownDispatch;
};

const toRows = (data: unknown): TalkDownStatusRow[] => {
  if (Array.isArray(data)) return data as TalkDownStatusRow[];
  if (data && typeof data === "object") {
    const results = (data as { results?: unknown }).results;
    if (Array.isArray(results)) return results as TalkDownStatusRow[];
    return [data as TalkDownStatusRow];
  }
  return [];
};

/**
 * GET the site's talk_down Command rows. The endpoint answers the latest
 * row as an object today; a list (bare or under `results`) is accepted too.
 */
export const fetchTalkDownStatus = async (
  siteId: string | number,
  signal?: AbortSignal
): Promise<TalkDownStatusRow[]> => {
  const response = await createApi("").get(
    `customer/${siteId}/action_status/?action=${TALK_DOWN_ACTION}`,
    { signal }
  );
  return toRows(response.data);
};

// Resolves after `ms`, or immediately once `signal` aborts.
const wait = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve) => {
    if (signal?.aborted) {
      resolve();
      return;
    }
    const done = () => {
      clearTimeout(timer);
      signal?.removeEventListener("abort", done);
      resolve();
    };
    const timer = setTimeout(done, ms);
    signal?.addEventListener("abort", done);
  });

export interface TalkDownPollOptions {
  intervalMs?: number;
  timeoutMs?: number;
  /** How long a 404 (no Command row yet) still counts as "not visible yet". */
  notFoundGraceMs?: number;
  signal?: AbortSignal;
}

/** 404 = the site has never had a talk_down Command; a fresh one shows
 *  within a moment of the 202. Past this it was not received. */
export const TALK_DOWN_NOT_FOUND_GRACE_MS = 3000;

export type TalkDownPollResult =
  | { kind: "done"; row: TalkDownStatusRow }
  | { kind: "timeout" }
  | { kind: "not_received" }
  | { kind: "aborted" };

/** One id, or several (a live session's row may carry either id). */
export type TalkDownRowId = number | string | Array<number | string>;

/** Ids compare as strings: v1 command ids are numbers, a live session id
 *  may be a uuid. */
export const rowMatches = (
  row: TalkDownStatusRow | null | undefined,
  ids: TalkDownRowId
): boolean => {
  if (row?.id == null) return false;
  const wanted = (Array.isArray(ids) ? ids : [ids])
    .filter((id) => id != null && id !== "")
    .map(String);
  return wanted.includes(String(row.id));
};

const isNotFound = (reason: unknown): boolean =>
  (reason as AxiosError | undefined)?.response?.status === 404;

/**
 * Polls action_status about once a second until the row whose id matches
 * the dispatched command_id leaves `pending`. The endpoint answers 404
 * until the site has ever had a talk_down Command: that counts as "not
 * visible yet" for the first few seconds after the 202, then as the
 * Command never having been received. Other errors count as pending.
 */
export const pollTalkDownOutcome = async (
  siteId: string | number,
  commandId: TalkDownRowId,
  options: TalkDownPollOptions = {}
): Promise<TalkDownPollResult> => {
  const intervalMs = options.intervalMs ?? TALK_DOWN_POLL_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? TALK_DOWN_POLL_TIMEOUT_MS;
  const notFoundGraceMs =
    options.notFoundGraceMs ?? TALK_DOWN_NOT_FOUND_GRACE_MS;
  const signal = options.signal;
  const attempts = Math.max(1, Math.ceil(timeoutMs / Math.max(1, intervalMs)));

  for (let attempt = 0; attempt < attempts; attempt++) {
    await wait(intervalMs, signal);
    if (signal?.aborted) return { kind: "aborted" };
    const elapsedMs = (attempt + 1) * intervalMs;
    try {
      const rows = await fetchTalkDownStatus(siteId, signal);
      if (signal?.aborted) return { kind: "aborted" };
      const row = rows.find((r) => rowMatches(r, commandId));
      if (row && row.status && row.status !== "pending") {
        return { kind: "done", row };
      }
    } catch (reason) {
      if (signal?.aborted) return { kind: "aborted" };
      if (isNotFound(reason) && elapsedMs >= notFoundGraceMs) {
        return { kind: "not_received" };
      }
      // No row yet (within grace) or a transient error: keep polling.
    }
  }
  return { kind: "timeout" };
};

const ERROR_CODE_COPY: Record<string, string> = {
  no_audio_output: "Camera has no speaker output",
  unreachable: "Couldn't reach the camera",
};

/** Plain-words outcome for a finished row (or null = timed out). */
export const describeTalkDownOutcome = (
  row: TalkDownStatusRow | null,
  cameraName: string
): { ok: boolean; message: string } => {
  if (!row) return { ok: false, message: TALK_DOWN_TIMEOUT_MESSAGE };
  const result = row.result ?? {};
  const played = row.status === "success" && result.status !== "failed";
  if (played) {
    const seconds =
      typeof result.seconds_sent === "number" && isFinite(result.seconds_sent)
        ? Math.round(result.seconds_sent * 10) / 10
        : null;
    const where = cameraName ? ` on ${cameraName}` : "";
    return {
      ok: true,
      message:
        seconds == null ? `Played${where}` : `Played ${seconds} s${where}`,
    };
  }
  const code = result.error_code ? String(result.error_code) : "";
  if (code && ERROR_CODE_COPY[code]) {
    return { ok: false, message: ERROR_CODE_COPY[code] };
  }
  const detail = result.detail ? String(result.detail).trim() : "";
  return { ok: false, message: detail || TALK_DOWN_CAMERA_FAILED_MESSAGE };
};

/**
 * Operator message for a failed POST. Each backend code maps to a short
 * line; a View Only user's plain DRF 403 carries no `error` and reads as
 * the permission message too. Nothing here retries.
 */
export const describeTalkDownSendError = (reason: unknown): string => {
  const err = reason as AxiosError<{ error?: string; detail?: string }> & {
    isProductionModeBlock?: boolean;
  };
  if (err?.isProductionModeBlock) {
    return err.response?.data?.detail || TALK_DOWN_FAILED_MESSAGE;
  }
  if (
    reason instanceof Error &&
    reason.message === TALK_DOWN_TOO_LARGE_MESSAGE
  ) {
    return TALK_DOWN_TOO_LARGE_MESSAGE;
  }
  const status = err?.response?.status;
  const code = err?.response?.data?.error;
  if (code === "talkdown_in_progress" || status === 409) {
    return TALK_DOWN_IN_PROGRESS_MESSAGE;
  }
  if (code === "invalid_audio" || status === 400) {
    return "The recording couldn't be used. Try recording again.";
  }
  if (code === "site_inactive") {
    return TALK_DOWN_SITE_INACTIVE_MESSAGE;
  }
  if (code === "talk_down_not_permitted" || status === 403) {
    return "You don't have permission to talk down on this camera.";
  }
  if (code === "talkdown_dispatch_failed") {
    return TALK_DOWN_DISPATCH_FAILED_MESSAGE;
  }
  if (code === "talkdown_relay_unavailable" || status === 503) {
    return TALK_DOWN_UNAVAILABLE_MESSAGE;
  }
  if (code === "dispatch_failed" || status === 502) {
    return "Couldn't send the talk-down to the site. Try again in a moment.";
  }
  if (status === 413) {
    return TALK_DOWN_TOO_LARGE_MESSAGE;
  }
  if (status === 401) {
    return "Your session has expired. Reload the page and try again.";
  }
  if (!status) {
    return TALK_DOWN_NETWORK_MESSAGE;
  }
  if (status >= 500) {
    return TALK_DOWN_SERVER_ERROR_MESSAGE;
  }
  return TALK_DOWN_REFUSED_MESSAGE;
};

/* -------------------------------------------------------------------- */
/* Live push-to-talk (ENG-1721), monitoring-api talk-down sessions. Only */
/* rendered behind showLiveTalkDown. Readiness and outcome come from the */
/* session's own STATUS endpoint, never admin's action_status.           */
/* -------------------------------------------------------------------- */

export type LiveTalkDownStatusValue = "starting" | "ready" | "ended" | "failed";

/** STATUS body (GET .../talk-down/{camera_id}/{session_id}/). */
export interface LiveTalkDownStatus {
  session_id: string;
  camera_id?: number | string;
  site_id?: number | string;
  mode?: "live";
  status: LiveTalkDownStatusValue;
  /** True while status is "ready" (the camera backchannel is listening). */
  ready: boolean;
  expires_at?: string | null;
  session_expires_at?: string | null;
  /** Server-side session cap (300 s); the client caps each hold at 60 s. */
  max_seconds?: number | null;
  /** The Job ends the session after this long with no frames. */
  idle_timeout_seconds?: number | null;
  publisher_wait_seconds?: number | null;
  stop_requested?: boolean | null;
  /** Counts silence frames too: session length, not talk time. */
  seconds_sent?: number | null;
  codec?: string | null;
  error_code?: string | null;
  ended_reason?: string | null;
  detail?: string | null;
}

/** 201 body of START: the status fields plus the publish credentials. */
export interface LiveTalkDownSession extends LiveTalkDownStatus {
  /** WHIP endpoint, already carrying `?jwt=<token>`: POST to it as-is.
   *  The token lives 120 s and is checked when the WHIP session is
   *  created, so publish immediately after the 201. */
  whip_url: string;
  /** Kept in the type; the WHIP POST does not send it (the URL carries it). */
  token?: string;
}

/** Client per-hold cap (the server's session cap is longer). */
export const LIVE_TALK_DOWN_MAX_SECONDS = 60;
/** An open session with nobody holding the button is torn down after this. */
export const LIVE_TALK_DOWN_IDLE_MS = 60_000;
/** STATUS poll: every second until ready, then every ~5 s while open. */
export const LIVE_TALK_DOWN_READY_INTERVAL_MS = 1000;
export const LIVE_TALK_DOWN_OPEN_INTERVAL_MS = 5000;
/** Client backstop; the server flips a silent Job to failed at 30 s. */
export const LIVE_TALK_DOWN_READY_TIMEOUT_MS = 40_000;
/**
 * After our DELETE the Job notices within ~3 s (it polls stop_requested),
 * but a Job still waiting for a publisher can hold the camera for up to
 * 20 s. The client polls STATUS for up to 25 s before it allows a new
 * START, so a retry does not hit 409 talkdown_in_progress.
 */
export const LIVE_TALK_DOWN_OUTCOME_INTERVAL_MS = 1000;
export const LIVE_TALK_DOWN_OUTCOME_TIMEOUT_MS = 25_000;
/** A 409 within this long of our own session on the camera is that session. */
export const LIVE_TALK_DOWN_RECENT_SESSION_MS = 30_000;

export const LIVE_TALK_DOWN_ENDED_MESSAGE = "Live talk-down ended";
export const LIVE_TALK_DOWN_STOPPED_MESSAGE = "Talk-down stopped unexpectedly";
export const LIVE_TALK_DOWN_NOT_FOUND_MESSAGE =
  "This camera wasn't found or isn't available to you.";

const LIVE_ERROR_CODE_COPY: Record<string, string> = {
  camera_unreachable: "Couldn't reach the camera",
  auth_failed: "Camera rejected the login",
  no_backchannel: "This camera doesn't support talk-down",
  unsupported_camera: "This camera doesn't support talk-down",
  unsupported_codec: "Camera audio format isn't supported",
  no_audio: "No audio reached the camera (check your microphone)",
  job_lost: LIVE_TALK_DOWN_STOPPED_MESSAGE,
  session_unavailable: LIVE_TALK_DOWN_STOPPED_MESSAGE,
  error: LIVE_TALK_DOWN_STOPPED_MESSAGE,
};

/**
 * Plain-words outcome for a finished live session. `null` = the session is
 * gone (STATUS 404) or never reported a final state: it simply ended.
 * seconds_sent counts silence frames too, so it is the session's length.
 */
export const describeLiveTalkDownOutcome = (
  status: LiveTalkDownStatus | null,
  cameraName: string
): { ok: boolean; message: string } => {
  if (!status) return { ok: true, message: LIVE_TALK_DOWN_ENDED_MESSAGE };
  const code = status.error_code ? String(status.error_code) : "";
  if (status.status === "failed" || code) {
    if (code && LIVE_ERROR_CODE_COPY[code]) {
      return { ok: false, message: LIVE_ERROR_CODE_COPY[code] };
    }
    return { ok: false, message: LIVE_TALK_DOWN_STOPPED_MESSAGE };
  }
  const seconds =
    typeof status.seconds_sent === "number" && isFinite(status.seconds_sent)
      ? Math.round(status.seconds_sent)
      : null;
  if (seconds == null)
    return { ok: true, message: LIVE_TALK_DOWN_ENDED_MESSAGE };
  const where = cameraName ? ` on ${cameraName}` : "";
  return { ok: true, message: `Session ended after ${seconds} s${where}` };
};

/** START refused because the camera already has a talk-down (409). */
export const isTalkDownInProgress = (reason: unknown): boolean => {
  const err = reason as AxiosError<{ error?: string }> | undefined;
  return (
    err?.response?.data?.error === "talkdown_in_progress" ||
    err?.response?.status === 409
  );
};

/** START error copy: v1's mapping plus the live-only 404. */
export const describeLiveTalkDownStartError = (reason: unknown): string => {
  const status = (reason as AxiosError | undefined)?.response?.status;
  if (status === 404) return LIVE_TALK_DOWN_NOT_FOUND_MESSAGE;
  return describeTalkDownSendError(reason);
};

/**
 * The one place that decides where the live endpoints live. Default: the
 * monitoring-api (the live-streaming runtime, same client as the live-view
 * mint). To move them to admin instead, set `client: "admin"` and return
 * `camera/${cameraId}/talk_down/live/` (and `.../live/${sessionId}/`).
 */
export const LIVE_TALK_DOWN_ENDPOINTS = {
  client: "monitoring" as "monitoring" | "admin",
  start: (cameraId: string | number) => `streaming/talk-down/${cameraId}/`,
  /** STATUS (GET) and STOP (DELETE) share this URL. */
  session: (cameraId: string | number, sessionId: string | number) =>
    `streaming/talk-down/${cameraId}/${sessionId}/`,
};

const liveClient = () =>
  LIVE_TALK_DOWN_ENDPOINTS.client === "admin"
    ? createApi("")
    : createMonitoringAPICall("");

/** POST start. Throws the axios error; use describeTalkDownSendError. */
export const startLiveTalkDown = async (
  cameraId: string | number
): Promise<LiveTalkDownSession> => {
  const response = await liveClient().post(
    LIVE_TALK_DOWN_ENDPOINTS.start(cameraId)
  );
  return response.data as LiveTalkDownSession;
};

/** DELETE the session. Callers treat it as best effort. */
export const stopLiveTalkDown = async (
  cameraId: string | number,
  sessionId: string | number
): Promise<void> => {
  await liveClient().delete(
    LIVE_TALK_DOWN_ENDPOINTS.session(cameraId, sessionId)
  );
};

/**
 * GET the session's STATUS. Resolves `null` on a 404 (the session is gone:
 * treat as ended). Other errors throw; pollers treat them as transient.
 */
export const fetchLiveTalkDownStatus = async (
  cameraId: string | number,
  sessionId: string | number,
  signal?: AbortSignal
): Promise<LiveTalkDownStatus | null> => {
  try {
    const response = await liveClient().get(
      LIVE_TALK_DOWN_ENDPOINTS.session(cameraId, sessionId),
      { signal }
    );
    return response.data as LiveTalkDownStatus;
  } catch (reason) {
    if (isNotFound(reason)) return null;
    throw reason;
  }
};

export const isLiveTalkDownFinished = (status: LiveTalkDownStatus | null) =>
  status == null || status.status === "ended" || status.status === "failed";

export interface LiveOutcomePollOptions {
  intervalMs?: number;
  timeoutMs?: number;
  signal?: AbortSignal;
}

export type LiveOutcomeResult =
  | { kind: "done"; status: LiveTalkDownStatus | null }
  | { kind: "timeout" }
  | { kind: "aborted" };

/**
 * After our DELETE, polls STATUS until the session reports ended/failed
 * (or 404 = gone). The Job notices stop_requested within ~3 s.
 */
export const pollLiveTalkDownOutcome = async (
  cameraId: string | number,
  sessionId: string | number,
  options: LiveOutcomePollOptions = {}
): Promise<LiveOutcomeResult> => {
  const intervalMs = options.intervalMs ?? LIVE_TALK_DOWN_OUTCOME_INTERVAL_MS;
  const timeoutMs = options.timeoutMs ?? LIVE_TALK_DOWN_OUTCOME_TIMEOUT_MS;
  const signal = options.signal;
  const attempts = Math.max(1, Math.ceil(timeoutMs / Math.max(1, intervalMs)));
  for (let attempt = 0; attempt < attempts; attempt++) {
    if (signal?.aborted) return { kind: "aborted" };
    try {
      const status = await fetchLiveTalkDownStatus(cameraId, sessionId, signal);
      if (signal?.aborted) return { kind: "aborted" };
      if (isLiveTalkDownFinished(status)) return { kind: "done", status };
    } catch {
      if (signal?.aborted) return { kind: "aborted" };
    }
    await wait(intervalMs, signal);
  }
  return signal?.aborted ? { kind: "aborted" } : { kind: "timeout" };
};
